import { useState } from 'react';
import { AnimatePresence } from 'motion/react';
import { Image } from '../../../../modules/design-system';
import type { TierBadge } from '../../../../modules/api';
import { DisclaimerModal } from '../../../../modules/disclaimer';
import { TierSelector } from '../../../classic/Pack/PackInfoCard/TierSelector';

export interface PackInfoCardProps {
  title: string;
  buybackPercent?: number;
  /**
   * In the modern template these are the sibling packs in the active
   * category, not the offer's own suitable_badges — each card represents
   * one slide of the slider.
   */
  tiers: TierBadge[];
  /**
   * Pack logo banner — moved to the sidebar on desktop layouts (Figma
   * `543:2118`). Mobile keeps it on the pedestal inside the slider.
   * `null` falls back to the title-only header.
   */
  packLogoImageUrl?: string | null;
  /**
   * Operator-overridable pack description from the backend. When present it
   * replaces the default buyback subtitle; `null`/empty keeps the default copy.
   */
  description?: string | null;
  /**
   * Fires with the clicked offer id. In the modern template the parent
   * wires this to `onSlideChange`, so a tier click focuses the slider
   * on that pack — no URL navigation, no page reload (unlike classic).
   */
  onTierSelect?: (poolOfferId: number) => void;
}

/**
 * Modern PackInfoCard — Figma `543:2118` (desktop sidebar header).
 *
 * Anatomy (top → bottom):
 *   1. **Logo banner** — the offer's `pack_logo_image_url` rendered big
 *      at the card's top (desktop only). Falls back to the title
 *      heading when the offer has no logo override or on mobile.
 *   2. **Subtitle** — `{buybackPercent}% FMV buyback available — or hold
 *      and redeem your vaulted graded card.` (bold percent).
 *   3. **Tier-card row** — one card per sibling pack in the active
 *      category. Active card mirrors the slider's current pack. Clicking
 *      a card focuses the slider on that pack (no URL update).
 */
export function PackInfoCard({
  title,
  buybackPercent = 90,
  tiers,
  packLogoImageUrl = null,
  description = null,
  onTierSelect,
}: PackInfoCardProps) {
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);

  return (
    <>
      {/* No `overflow-hidden` here on desktop so the pack logo can bleed
          above the card edge (Figma 543:2118 — logo's bottom overlaps the
          card's top). When the logo is present the card is pushed down by the
          56px the logo peeks up. The whole PackInfoCard is desktop-only
          (`hidden @[800px]:block`, container query — must switch in sync
          with the PackLayout grid) — mobile shows tier info on the slider
          pedestal and the BottomAction strip instead. */}
      <div
        data-co-component="PackInfoCard"
      className={
        'hidden @[800px]:block ' +
        'relative [border-radius:var(--co-PackInfoCard-borderRadius,24px)] text-white ' +
        'px-[14px] py-5 ' +
        (packLogoImageUrl ? '@[800px]:pt-0 @[800px]:mt-[56px]' : '')
      }
      // Single operator-driven gradient. `color-mix` applies the bgOpacity
      // knob to each stop. Sleek's defaults are the flat Subtler card (no
      // glass wash, no blur, a 1% hairline) — the mobile look, carried to
      // the desktop sidebar; the knobs can still bring the glass back.
      style={{
        backgroundImage:
          'linear-gradient(' +
          'var(--co-PackInfoCard-bgDirection, to bottom), ' +
          'color-mix(in srgb, var(--co-PackInfoCard-bgFrom, #1A1B1F) ' +
          'calc(var(--co-PackInfoCard-bgOpacity, 1) * 100%), transparent) 0%, ' +
          'color-mix(in srgb, var(--co-PackInfoCard-bgTo, #1A1B1F) ' +
          'calc(var(--co-PackInfoCard-bgOpacity, 1) * 100%), transparent) 100%)',
        backdropFilter: 'blur(var(--co-PackInfoCard-glassBlur, 0px))',
        WebkitBackdropFilter: 'blur(var(--co-PackInfoCard-glassBlur, 0px))',
        borderStyle: 'solid',
        borderWidth: 'var(--co-PackInfoCard-borderWidth, 1px)',
        borderColor: 'var(--co-PackInfoCard-borderColor, rgba(255,255,255,0.01))',
      }}
    >
      <div className="flex flex-col items-center gap-3 text-center text-white">
        {packLogoImageUrl && (
          // Logo "peeks" out of the card: pulled up by 56px via a
          // negative top-margin so the lower third still sits on the
          // card surface, matching Figma 543:2118. Hidden below desktop.
          <div className="hidden @[800px]:block relative w-[78%] aspect-[233/117] -mt-[56px] [filter:drop-shadow(0_18px_28px_rgba(0,0,0,0.45))]">
            <Image
              src={packLogoImageUrl}
              alt={title}
              className="absolute inset-0"
              imgClassName="absolute inset-0 h-full w-full object-contain"
            />
          </div>
        )}

        <h2
          className={
            'm-0 text-center font-semibold ' +
            'leading-[28px] tracking-[-0.45px] ' +
            (packLogoImageUrl ? '@[800px]:hidden' : '')
          }
          style={{
            color: 'var(--co-PackInfoCard-titleColor, #ffffff)',
            fontFamily: 'var(--co-PackInfoCard-fontFamily), Inter, system-ui, sans-serif',
            fontSize: 'var(--co-PackInfoCard-titleSize, 20px)',
          }}
        >
          {title || 'Pack title'}
        </h2>

        <p
          className="m-0 max-w-[309px] text-center leading-[20px] tracking-[-0.1px] font-normal"
          style={{
            color: 'var(--co-PackInfoCard-subtitleColor, #6D707C)',
            fontFamily: 'var(--co-PackInfoCard-fontFamily), Inter, system-ui, sans-serif',
            fontSize: 'var(--co-PackInfoCard-subtitleSize, 14px)',
          }}
        >
          {description ? (
            description
          ) : (
            <>
                All professionally graded cards.
                <br />
                <span className="font-semibold">{buybackPercent}% FMV Buyback</span> available.
            </>
          )}{' '}
          {/* Opens the box-withdrawal disclaimer (Figma 706:1668). Inline
              underlined link, same colour as the surrounding subtitle. */}
          <button
            type="button"
            onClick={() => setDisclaimerOpen(true)}
            className="cursor-pointer underline underline-offset-2 transition-opacity hover:opacity-80"
            style={{ color: 'inherit' }}
          >
            Disclaimer
          </button>
        </p>
      </div>

      {tiers.length > 0 && (
        <div className="pt-4">
          {/* `layout="desktop"`: this card only renders inside the ≥800px
              sidebar, but TierCard's own container query switches at 900 —
              without the override 800–900px viewports would get the compact
              mobile pill inside the desktop sidebar. */}
          <TierSelector
            badges={tiers}
            layout="desktop"
            {...(onTierSelect ? { onSelect: onTierSelect } : {})}
          />
        </div>
      )}
      </div>

      <AnimatePresence>
        {disclaimerOpen && (
          <DisclaimerModal onClose={() => setDisclaimerOpen(false)} />
        )}
      </AnimatePresence>
    </>
  );
}
